import { GoogleAuthProvider, OAuthProvider } from "firebase/auth";
import UAParser from "ua-parser-js";
import { isSafeNextPath } from "@stanza/shared/safe-next-path";
import { getPageRoute } from "src/services";

/** sessionStorage key written before `signInWithRedirect` to restore query params on return. */
export const SIGN_IN_REDIRECT_PARAMS_KEY = "signInRedirectParams";

/** sessionStorage key written before `signInWithRedirect` with the provider name, read back on return. */
export const SIGN_IN_REDIRECT_PROVIDER_KEY = "signInRedirectProvider";

/**
 * sessionStorage key set before `signInWithRedirect` so the sign-in buttons
 * stay disabled when the app remounts after the OAuth redirect while
 * `getRedirectResult` is still processing. Cleared by UseAuth when
 * `checkSignIn` completes (success, failure, or null result).
 */
export const SIGN_IN_REDIRECT_IN_PROGRESS_KEY = "signInRedirectInProgress";

/**
 * sessionStorage key set by UpdateRelayEmail before calling signInWithRedirect
 * so UseAuth skips its own getRedirectResult call and doesn't race to consume
 * the credential first.
 */
export const RELAY_REDIRECT_KEY = "relayMigrationPending";

/**
 * sessionStorage key holding where a sign-in dialog's caller wants to land
 * after a redirect sign-in, as `{ route, state }`.
 */
export const SIGN_IN_DESTINATION_KEY = "signInDestination";

/** Query params Firebase appends to an email sign-in link. */
const EMAIL_LINK_PARAMS = ["apiKey", "oobCode", "mode", "lang", "continueUrl", "tenantId"];

/**
 * Query params with the Firebase email-link params removed. `email` stays on
 * the calendar verify page, which reads it.
 *
 * @param {string} search `window.location.search`.
 * @param {string} pathname `window.location.pathname`.
 * @returns {URLSearchParams}
 */
export function withoutEmailLinkParams(search, pathname) {
  const params = new URLSearchParams(search);
  EMAIL_LINK_PARAMS.forEach((key) => params.delete(key));
  const verifyPage = getPageRoute("help", "SUBSCRIBING_CALENDAR_VERIFY");
  const verifyPrefix = verifyPage.slice(0, verifyPage.indexOf("/:handle/:calendarId"));
  if (!pathname.includes(verifyPrefix)) params.delete("email");
  return params;
}

/**
 * Where to send the user after sign-in: `forward`, else a validated `next`
 * (the magic-link fallback sends `/signin?next=`).
 *
 * @param {URLSearchParams} searchParams
 * @returns {string | null}
 */
export function postSignInDestination(searchParams) {
  const forward = searchParams.get("forward");
  if (forward) return forward;
  const next = searchParams.get("next");
  return isSafeNextPath(next) ? next : null;
}

/**
 * Remembers (or, with null, forgets) where to go after the next redirect
 * sign-in. A redirect reloads the app and drops the dialog, so this outlives it.
 *
 * @param {{ route: string, state?: object } | null} destination
 */
export function setSignInDestination(destination) {
  try {
    if (destination) sessionStorage.setItem(SIGN_IN_DESTINATION_KEY, JSON.stringify(destination));
    else sessionStorage.removeItem(SIGN_IN_DESTINATION_KEY);
  } catch {
    // Storage unavailable: popup sign-in still lands through the caller's onSignIn.
  }
}

/**
 * Reads and forgets the remembered post-sign-in destination.
 *
 * @returns {{ route: string, state?: object } | null} Null when none, or when it isn't a safe in-app path.
 */
export function takeSignInDestination() {
  try {
    const raw = sessionStorage.getItem(SIGN_IN_DESTINATION_KEY);
    sessionStorage.removeItem(SIGN_IN_DESTINATION_KEY);
    const destination = raw ? JSON.parse(raw) : null;
    return isSafeNextPath(destination?.route) ? destination : null;
  } catch {
    return null;
  }
}

/**
 * Returns true when the current browser should use Firebase redirect sign-in
 * instead of popup. iOS blocks all OAuth popups; Safari on macOS blocks them
 * by default. Chrome and Firefox on macOS do not block popups and use the
 * faster popup path.
 *
 * Always returns false when the Cypress popup stub is active so tests keep
 * driving the popup path via the `__cypressSignInStub` mechanism.
 *
 * @returns {boolean}
 */
export function shouldUseRedirect() {
  if (
    typeof window !== "undefined" &&
    typeof window.__cypressSignInStub === "function"
  ) {
    return false;
  }
  const parser = new UAParser();
  const os = parser.getOS().name;
  if (os === "iOS") return true;
  if (os === "Mac OS") {
    const browser = parser.getBrowser().name;
    return browser === "Safari" || browser === "Mobile Safari";
  }
  return false;
}

/**
 * Extracts the OAuth credential carried by an
 * `auth/account-exists-with-different-credential` rejection.
 *
 * @param {import("firebase/auth").AuthError} error
 * @param {import("firebase/auth").AuthProvider} provider Provider the rejected attempt used.
 * @returns {import("firebase/auth").OAuthCredential | null}
 */
export function pendingCredentialFromError(error, provider) {
  if (!error || !provider) return null;
  const cred =
    provider instanceof GoogleAuthProvider
      ? GoogleAuthProvider.credentialFromError(error)
      : OAuthProvider.credentialFromError(error);
  // OAuthProvider builds the credential from the token response, not from the
  // provider it was called on, so enforce that they agree.
  return cred && cred.providerId === provider.providerId ? cred : null;
}

/**
 * Whether the signed-in Firebase user already has the given provider linked.
 *
 * @param {import("firebase/auth").User} user
 * @param {string} providerId
 * @returns {boolean}
 */
export function isProviderLinked(user, providerId) {
  if (!providerId) return false;
  return Boolean(
    user?.providerData?.some((entry) => entry?.providerId === providerId)
  );
}
